import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faWrench,
  faArrowRight,
  faSave,
  faBridge,
  faCalendarAlt,
  faPercent,
  faDollarSign,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import InlineFieldTooltip from "../components/InlineFieldTooltip";
import {
  D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY,
  D610_DEGRADATION_RATE_SETTING_KEY,
  D610_SCORE_METADATA_SETTING_KEY,
  getD610ColorForScore,
  interpolateD610ScoreFromSettings,
  normalizeD610ScoreMetadataMatrix,
} from "../utils/d610Degradation";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [hoveredSnapshotId, setHoveredSnapshotId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [snapshotAnalytics, setSnapshotAnalytics] = useState(null);
  const [hoverPopup, setHoverPopup] = useState(null);
  const [heatmapMode, setHeatmapMode] = useState("percent");
  const [d610Settings, setD610Settings] = useState({});
  const [d610SettingsLoading, setD610SettingsLoading] = useState(false);
  const stickyNavRef = useRef(null);
  const summarySectionRef = useRef(null);
  const scenarioSectionRef = useRef(null);
  const budgetBarsSectionRef = useRef(null);
  const budgetChartSectionRef = useRef(null);
  const bridgeChartSectionRef = useRef(null);
  const heatmapSectionRef = useRef(null);

  const getSnapshotTimestamp = useCallback((snapshot) => {
    const raw =
      snapshot?.savedAt ||
      snapshot?.createdAt ||
      snapshot?.saved_at ||
      snapshot?.created_at;
    const parsed = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  useEffect(() => {
    if (!selectedSnapshotId) {
      setSelectedSnapshot(null);
      setSnapshotAnalytics(null);
      return;
    }
    fetchSnapshotDetails(selectedSnapshotId);
    fetchSnapshotAnalytics(selectedSnapshotId);
  }, [selectedSnapshotId]);

  const fetchSnapshots = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/simulations/snapshots");
      if (res.data?.success && Array.isArray(res.data.data)) {
        const sorted = [...res.data.data].sort((a, b) => {
          const bDate = getSnapshotTimestamp(b);
          const aDate = getSnapshotTimestamp(a);
          if (bDate !== aDate) return bDate - aDate;

          const bId = Number(b?.id || 0);
          const aId = Number(a?.id || 0);
          return bId - aId;
        });
        setSnapshots(sorted);
        if (sorted.length > 0) {
          setSelectedSnapshotId(sorted[0].id);
        }
      } else {
        setSnapshots([]);
      }
    } catch (error) {
      console.error("Error fetching snapshots:", error);
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  }, [getSnapshotTimestamp]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  useEffect(() => {
    const fetchD610Settings = async () => {
      try {
        setD610SettingsLoading(true);
        const res = await api.get("/settings/categories", {
          skipGlobalLoading: true,
        });
        if (res.data?.success) {
          setD610Settings(res.data?.data?.d610_degradation_rate || {});
        } else {
          setD610Settings({});
        }
      } catch (error) {
        console.error("Error fetching D610 settings for dashboard:", error);
        setD610Settings({});
      } finally {
        setD610SettingsLoading(false);
      }
    };

    fetchD610Settings();
  }, []);

  const fetchSnapshotDetails = async (snapshotId) => {
    try {
      setDetailLoading(true);
      const res = await api.get(`/simulations/snapshots/${snapshotId}`);
      if (res.data?.success && res.data.data) {
        setSelectedSnapshot(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching snapshot details:", error);
      setSelectedSnapshot(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchSnapshotAnalytics = async (snapshotId) => {
    try {
      setAnalyticsLoading(true);
      const res = await api.get(
        `/simulations/snapshots/${snapshotId}/analytics`,
      );
      if (res.data?.success && res.data.data) {
        setSnapshotAnalytics(res.data.data);
      } else {
        setSnapshotAnalytics(null);
      }
    } catch (error) {
      console.error("Error fetching snapshot analytics:", error);
      setSnapshotAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const selectedYearlyTotals = useMemo(() => {
    if (!selectedSnapshot || !Array.isArray(selectedSnapshot.yearlyTotals))
      return [];
    return selectedSnapshot.yearlyTotals.slice(0, 15);
  }, [selectedSnapshot]);

  const budgetEvolutionData = useMemo(() => {
    const rows = snapshotAnalytics?.yearlyBudgetEvolution;
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => ({
      year: row.calendarYear || row.yearIndex,
      totalCost: Number(row.totalCost || 0),
      costWash: Number(row.costWash || 0),
      costTU: Number(row.costTU || 0),
      costOC: Number(row.costOC || 0),
      costRE: Number(row.costRE || 0),
    }));
  }, [snapshotAnalytics]);

  const degradationEvolutionData = useMemo(() => {
    const rows = snapshotAnalytics?.bridgeDegradationEvolution;
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => ({
      year: row.calendarYear || row.yearIndex,
      weightedDegradationPercent: Number(row.weightedDegradationPercent || 0),
      effectiveDegradedAreaPercent: Number(
        row.effectiveDegradedAreaPercent || 0,
      ),
      degradedArea: Number(row.degradedArea || 0),
    }));
  }, [snapshotAnalytics]);

  const directionHeatmapRows = useMemo(() => {
    const rows = Array.isArray(snapshotAnalytics?.directionDegradationEvolution)
      ? snapshotAnalytics.directionDegradationEvolution
      : [];
    const bridgeRows = Array.isArray(snapshotAnalytics?.bridgeDegradationEvolution)
      ? snapshotAnalytics.bridgeDegradationEvolution
      : [];

    if (!bridgeRows.length) return rows;

    const entireBridgeYearly = bridgeRows.map((row) => ({
      direction: "Entire Bridge Status",
      yearIndex: Number(row?.yearIndex || 0),
      calendarYear: Number(row?.calendarYear || 0),
      totalArea: Number(snapshotAnalytics?.bridgeTotalArea || 0),
      weightedDegradationPercent: Number(row?.weightedDegradationPercent || 0),
      averageDegradationPercent: Number(row?.weightedDegradationPercent || 0),
      degradedArea: Number(row?.degradedArea || 0),
      effectiveDegradedAreaPercent: Number(
        row?.effectiveDegradedAreaPercent || 0,
      ),
    }));

    const avgWeighted =
      entireBridgeYearly.length > 0
        ? entireBridgeYearly.reduce(
            (sum, yearCell) =>
              sum + Number(yearCell.weightedDegradationPercent || 0),
            0,
          ) / entireBridgeYearly.length
        : 0;

    const peakEffective =
      entireBridgeYearly.length > 0
        ? Math.max(
            ...entireBridgeYearly.map((yearCell) =>
              Number(yearCell.effectiveDegradedAreaPercent || 0),
            ),
          )
        : 0;

    return [
      ...rows,
      {
        direction: "Entire Bridge Status",
        totalArea: Number(snapshotAnalytics?.bridgeTotalArea || 0),
        avgWeightedDegradationPercent: avgWeighted,
        avgDegradationPercent: avgWeighted,
        peakEffectiveDegradedAreaPercent: peakEffective,
        yearly: entireBridgeYearly,
      },
    ];
  }, [snapshotAnalytics]);

  const heatmapYears = useMemo(() => {
    if (!directionHeatmapRows.length) return [];
    const first = directionHeatmapRows[0]?.yearly || [];
    return first.map((entry) => entry.calendarYear || entry.yearIndex);
  }, [directionHeatmapRows]);

  const maxDirectionPercent = useMemo(() => {
    if (!directionHeatmapRows.length) return 0;
    let max = 0;
    directionHeatmapRows.forEach((row) => {
      (row.yearly || []).forEach((yearCell) => {
        max = Math.max(
          max,
          Number(
            yearCell.averageDegradationPercent ??
              yearCell.effectiveDegradedAreaPercent ??
              0,
          ),
        );
      });
    });
    return max;
  }, [directionHeatmapRows]);

  const hasD610HeatmapSettings = useMemo(
    () =>
      Boolean(d610Settings[D610_DEGRADATION_RATE_SETTING_KEY]) &&
      Boolean(d610Settings[D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY]) &&
      Boolean(d610Settings[D610_SCORE_METADATA_SETTING_KEY]),
    [d610Settings],
  );

  const d610ScoreMetadataMatrix = useMemo(() => {
    if (!hasD610HeatmapSettings) {
      return [];
    }

    return normalizeD610ScoreMetadataMatrix(
      d610Settings[D610_SCORE_METADATA_SETTING_KEY]?.value,
    );
  }, [d610Settings, hasD610HeatmapSettings]);

  const maxYearlyCost = useMemo(() => {
    if (!selectedYearlyTotals.length) return 0;
    return Math.max(
      ...selectedYearlyTotals.map((y) => Number(y.totalCost || 0)),
    );
  }, [selectedYearlyTotals]);

  const formatCurrency = (value) => {
    const numeric = Number(value || 0);
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(numeric);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString();
  };

  const formatSimulationName = (name, fallbackId = null) => {
    const source =
      name || (fallbackId ? `Simulation #${fallbackId}` : "Simulation");
    return source.replace(/^New Simulation Engine/i, "Simulation");
  };

  const pluralizeLabel = (label, count) => {
    const trimmed = String(label || "Selected Item").trim() || "Selected Item";
    if (count === 1) return trimmed;
    if (/(s|x|z|ch|sh)$/i.test(trimmed)) return `${trimmed}es`;
    if (/[^aeiou]y$/i.test(trimmed)) return `${trimmed.slice(0, -1)}ies`;
    return `${trimmed}s`;
  };

  const formatArea = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "-";
    return `${numeric.toLocaleString("en-CA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} m²`;
  };

  const getSnapshotSelectionSummary = (snapshot) =>
    snapshot?.selectionSummary || null;

  const getSelectedEntityDisplayLabel = (snapshot) => {
    const selectionSummary = getSnapshotSelectionSummary(snapshot);
    return pluralizeLabel(
      selectionSummary?.selectedEntityLabel || "Item",
      Number(selectionSummary?.selectedEntityCount || 0),
    );
  };

  const getSelectedEntityCountText = (snapshot) => {
    const selectionSummary = getSnapshotSelectionSummary(snapshot);
    const count = Number(selectionSummary?.selectedEntityCount);
    if (!Number.isFinite(count)) return "-";
    return `${count.toLocaleString("en-CA")} ${getSelectedEntityDisplayLabel(snapshot)}`;
  };

  const getSelectedEntityCountValue = (snapshot) => {
    const selectionSummary = getSnapshotSelectionSummary(snapshot);
    const count = Number(selectionSummary?.selectedEntityCount);
    if (!Number.isFinite(count)) return "-";
    return count.toLocaleString("en-CA");
  };

  const getEstimatedPaintedAreaText = (snapshot) => {
    const selectionSummary = getSnapshotSelectionSummary(snapshot);
    return formatArea(selectionSummary?.estimatedPaintedArea);
  };

  const handleDeleteSnapshot = async (snapshotId) => {
    const target = snapshots.find((s) => s.id === snapshotId);
    const label = formatSimulationName(target?.name, snapshotId);
    const confirmed = window.confirm(`Delete ${label}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await api.delete(`/simulations/${snapshotId}`);
      const nextSnapshots = snapshots.filter((s) => s.id !== snapshotId);
      setSnapshots(nextSnapshots);

      if (selectedSnapshotId === snapshotId) {
        const nextId = nextSnapshots[0]?.id || null;
        setSelectedSnapshotId(nextId);
      }

      if (hoveredSnapshotId === snapshotId) {
        setHoveredSnapshotId(null);
        setHoverPopup(null);
      }
    } catch (error) {
      console.error("Error deleting snapshot:", error);
      window.alert(
        error.response?.data?.message || "Failed to delete simulation",
      );
    }
  };

  const formatDamageSplit = (split) => {
    if (!split) return "-";
    const a = Number(split.A);
    const b = Number(split.B);
    const c = Number(split.C);
    if (Number.isNaN(a) || Number.isNaN(b) || Number.isNaN(c)) return "-";
    return `${a}/${b}/${c}`;
  };

  const getDamageSplitSummary = (scenario) => {
    const byCategory = scenario?.damageSplitByCategory;
    if (byCategory && typeof byCategory === "object") {
      const categories = Object.keys(byCategory).filter(
        (cat) => byCategory[cat],
      );
      if (categories.length > 0) {
        return categories
          .sort()
          .map((cat) => `${cat} ${formatDamageSplit(byCategory[cat])}`)
          .join(" | ");
      }
    }

    return formatDamageSplit(scenario?.damageSplit);
  };

  const getHeatColor = useCallback((value) => {
    const numeric = Number(value || 0);
    const ratio =
      maxDirectionPercent > 0 ? Math.min(1, numeric / maxDirectionPercent) : 0;
    const red = 239;
    const green = Math.round(245 - ratio * 120);
    const blue = Math.round(245 - ratio * 170);
    return `rgb(${red}, ${green}, ${blue})`;
  }, [maxDirectionPercent]);

  const isNearZeroForDisplay = (value, digits = 2) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return true;
    return Math.abs(numeric) < 0.5 * 10 ** -digits;
  };

  const formatDisplayNumber = (value, digits = 2, zeroPlaceholder = "-") => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return zeroPlaceholder;
    if (isNearZeroForDisplay(numeric, digits)) return zeroPlaceholder;
    return numeric.toFixed(digits);
  };

  const formatBridgeChartMetric = (value, name) => {
    const numeric = Number(value || 0);
    const isAreaMetric = String(name || "").includes("(m²)");

    if (!Number.isFinite(numeric) || isNearZeroForDisplay(numeric, 2)) {
      return isAreaMetric ? "-" : "-";
    }

    return isAreaMetric ? numeric.toFixed(2) : `${numeric.toFixed(2)}%`;
  };

  const getScrollContainer = useCallback((element) => {
    let current = element?.parentElement;

    while (current) {
      const styles = window.getComputedStyle(current);
      const overflowY = styles.overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        current.scrollHeight > current.clientHeight
      ) {
        return current;
      }
      current = current.parentElement;
    }

    return null;
  }, []);

  const scrollToDashboardSection = useCallback(
    (targetRef) => {
      if (!targetRef?.current) return;

      const targetElement = targetRef.current;
      const scrollContainer = getScrollContainer(targetElement);
      const stickyOffset = (stickyNavRef.current?.offsetHeight || 0) + 12;

      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const targetScrollTop =
          scrollContainer.scrollTop +
          (targetRect.top - containerRect.top) -
          stickyOffset;

        scrollContainer.scrollTo({
          top: Math.max(targetScrollTop, 0),
          behavior: "smooth",
        });
        return;
      }

      const targetTop = targetElement.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: Math.max(targetTop - stickyOffset, 0),
        behavior: "smooth",
      });
    },
    [getScrollContainer],
  );

  const dashboardSectionLinks = useMemo(
    () => [
      { id: "summary", label: "Summary", ref: summarySectionRef },
      { id: "scenario", label: "Scenario", ref: scenarioSectionRef },
      { id: "budget-bars", label: "Budget 15Y", ref: budgetBarsSectionRef },
      { id: "budget-chart", label: "Budget Chart", ref: budgetChartSectionRef },
      { id: "bridge-chart", label: "Bridge Degradation", ref: bridgeChartSectionRef },
      { id: "heatmap", label: "Direction Heatmap", ref: heatmapSectionRef },
    ],
    [],
  );

  const getReadableTextColor = (backgroundColor) => {
    const normalized = String(backgroundColor || "").trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(normalized)) {
      return "#111827";
    }

    const hex = normalized.slice(1);
    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

    return luminance > 0.62 ? "#111827" : "#F9FAFB";
  };

  const directionHeatmapDisplayRows = useMemo(() => {
    const getPercentValue = (yearCell) =>
      Number(
        yearCell?.averageDegradationPercent ??
          yearCell?.effectiveDegradedAreaPercent ??
          0,
      );

    const getD610PaletteForPercent = (percentValue, contextLabel) => {
      if (!hasD610HeatmapSettings) {
        return null;
      }

      try {
        const scoreValue = interpolateD610ScoreFromSettings(
          percentValue,
          d610Settings,
        );
        const backgroundColor =
          getD610ColorForScore(scoreValue, d610ScoreMetadataMatrix) ||
          "#FFFFFF";

        return {
          scoreValue,
          backgroundColor,
          textColor: getReadableTextColor(backgroundColor),
        };
      } catch (error) {
        console.error(
          `Error building D610 dashboard heatmap ${contextLabel}:`,
          error,
        );
        return null;
      }
    };

    return directionHeatmapRows.map((row) => {
      const averagePercent = Number(
        row?.avgDegradationPercent ?? row?.avgWeightedDegradationPercent ?? 0,
      );
      const averagePalette = getD610PaletteForPercent(averagePercent, "average");

      const yearly = (row.yearly || []).map((yearCell) => {
        const percentValue = getPercentValue(yearCell);
        const palette = getD610PaletteForPercent(percentValue, "cell");

        if (heatmapMode === "d610" && hasD610HeatmapSettings) {
          return {
            ...yearCell,
            percentValue,
            displayValue: palette?.scoreValue,
            backgroundColor: palette?.backgroundColor || "#FFFFFF",
            textColor: palette?.textColor || "#111827",
            title: `${row.direction} ${yearCell.calendarYear}: ${
              palette?.scoreValue != null ? palette.scoreValue.toFixed(2) : "-"
            } D610 (${percentValue.toFixed(2)}%)`,
          };
        }

        return {
          ...yearCell,
          percentValue,
          displayValue: percentValue,
          backgroundColor: palette?.backgroundColor || getHeatColor(percentValue),
          textColor: palette?.textColor || "#111827",
          title: `${row.direction} ${yearCell.calendarYear}: ${percentValue.toFixed(2)}%${
            palette?.scoreValue != null
              ? ` (${palette.scoreValue.toFixed(2)} D610)`
              : ""
          }`,
        };
      });

      const averageScore = averagePalette?.scoreValue ?? null;

      return {
        ...row,
        yearly,
        averagePercent,
        averageScore,
        averageBackgroundColor:
          averagePalette?.backgroundColor || getHeatColor(averagePercent),
        averageTextColor: averagePalette?.textColor || "#111827",
        averageTitle:
          averageScore != null
            ? `${row.direction}: ${averagePercent.toFixed(2)}% (${averageScore.toFixed(2)} D610)`
            : `${row.direction}: ${averagePercent.toFixed(2)}%`,
      };
    });
  }, [
    d610ScoreMetadataMatrix,
    d610Settings,
    directionHeatmapRows,
    getHeatColor,
    hasD610HeatmapSettings,
    heatmapMode,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50">
      <div className="w-full pb-2">
        <div className="mb-6 mt-2 flex items-start gap-2">
          <p className="text-gray-600">
            Saved simulation snapshots and scenario evolution. Welcome back,{" "}
            {user?.fullName || user?.username}.
          </p>
          <InlineFieldTooltip
            page="dashboard"
            section="page_description"
            fieldKey="dashboard_overview"
          />
        </div>

        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4 lg:self-start lg:sticky lg:top-0 xl:col-span-3">
            <div className="flex flex-col overflow-hidden rounded-lg border bg-white shadow-sm lg:max-h-[calc(100vh-6rem)]">
              <div className="px-4 py-3 border-b bg-gray-50 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faSave} className="text-blue-600" />
                <h2 className="font-semibold text-gray-900">Saved Snapshots</h2>
              </div>
              <span className="text-xs text-gray-500">{snapshots.length}</span>
              </div>

              <div className="flex-1 space-y-2 p-3 lg:overflow-y-auto">
                {snapshots.length === 0 && (
                  <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded border">
                    No saved snapshots yet. Run a simulation and save
                    it from simulation results.
                  </div>
                )}

                {snapshots.map((snapshot) => {
                  const isSelected = selectedSnapshotId === snapshot.id;

                  return (
                    <div
                      key={snapshot.id}
                      className={`relative border rounded-md p-3 cursor-pointer transition-colors ${isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}
                      onClick={() => setSelectedSnapshotId(snapshot.id)}
                      onMouseEnter={(event) => {
                        const rect = event.currentTarget.getBoundingClientRect();
                        setHoveredSnapshotId(snapshot.id);
                        setHoverPopup({
                          snapshot,
                          top: rect.top,
                          left: rect.right + 12,
                        });
                      }}
                      onMouseLeave={() => {
                        setHoveredSnapshotId(null);
                        setHoverPopup(null);
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {formatSimulationName(snapshot.name, snapshot.id)}
                        </div>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete simulation"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteSnapshot(snapshot.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      <div className="mt-1 grid grid-cols-[minmax(0,1fr)_max-content] gap-x-4 gap-y-1 text-xs text-gray-500">
                        <div className="inline-flex min-w-0 items-center gap-1 text-gray-600">
                          <FontAwesomeIcon icon={faBridge} className="shrink-0" />
                          <span className="truncate">
                            {snapshot.bridgeName || "Unknown bridge"}
                          </span>
                        </div>
                        <div className="justify-self-end text-right">
                          {getSelectedEntityCountText(snapshot)}
                        </div>
                        <div className="inline-flex items-center gap-1">
                          <FontAwesomeIcon icon={faCalendarAlt} className="shrink-0" />
                          <span>
                            {formatDate(snapshot.savedAt || snapshot.createdAt)}
                          </span>
                        </div>
                        <div className="justify-self-end text-right">
                          {getEstimatedPaintedAreaText(snapshot) === "-"
                            ? "-"
                            : `${getEstimatedPaintedAreaText(snapshot)} painted`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-6 lg:col-span-8 lg:self-start xl:col-span-9">
            {!selectedSnapshotId && (
              <div className="bg-white rounded-lg border shadow-sm p-8 text-center text-gray-600">
                Select a snapshot from the left panel to view dashboard details.
              </div>
            )}

            {selectedSnapshotId && (
              <>
                <div ref={stickyNavRef} className="sticky top-0 z-20 -mt-2">
                  <div className="overflow-x-auto rounded-lg border border-blue-200 bg-blue-100/95 px-3 py-3 shadow-sm backdrop-blur-sm">
                    <div className="flex min-w-max items-center gap-2">
                      {dashboardSectionLinks.map((section) => (
                        <button
                          key={section.id}
                          type="button"
                          onClick={() => scrollToDashboardSection(section.ref)}
                          className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                        >
                          {section.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div ref={summarySectionRef} className="bg-white rounded-lg border shadow-sm p-5 scroll-mt-28">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {formatSimulationName(
                          selectedSnapshot?.name,
                          selectedSnapshotId,
                        )}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedSnapshot?.bridgeName || "Unknown bridge"} •
                        Saved{" "}
                        {formatDate(
                          selectedSnapshot?.savedAt ||
                            selectedSnapshot?.createdAt,
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate("/enhanced-simulation-results")}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    >
                      <FontAwesomeIcon icon={faChartLine} />
                      <span>Run Simulation</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="bg-blue-50 border border-blue-100 rounded p-3">
                      <div className="text-xs text-blue-700 uppercase tracking-wide">
                        Total Cost
                      </div>
                      <div className="text-lg font-bold text-blue-900 mt-1">
                        {formatCurrency(selectedSnapshot?.summary?.totalCost)}
                      </div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 rounded p-3">
                      <div className="text-xs text-emerald-700 uppercase tracking-wide">
                        Avg Yearly Cost
                      </div>
                      <div className="text-lg font-bold text-emerald-900 mt-1">
                        {formatCurrency(
                          selectedSnapshot?.summary?.avgYearlyCost,
                        )}
                      </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded p-3">
                      <div className="text-xs text-amber-700 uppercase tracking-wide">
                        Peak Year Cost
                      </div>
                      <div className="text-lg font-bold text-amber-900 mt-1">
                        {formatCurrency(selectedSnapshot?.summary?.peakCost)}
                      </div>
                    </div>
                  </div>
                </div>

                <div ref={scenarioSectionRef} className="bg-white rounded-lg border shadow-sm p-5 scroll-mt-28">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Scenario Parameters
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
                    <div className="border rounded p-3">
                      <div className="text-xs text-gray-500">Time Horizon</div>
                      <div className="font-semibold text-gray-900 mt-1">
                        {selectedSnapshot?.scenario?.timeHorizonYears ?? "-"}
                      </div>
                    </div>
                    <div className="border rounded p-3">
                      <div className="text-xs text-gray-500">
                        Inflation Rate
                      </div>
                      <div className="font-semibold text-gray-900 mt-1 flex items-center gap-1">
                        <FontAwesomeIcon
                          icon={faPercent}
                          className="text-gray-400"
                        />{" "}
                        {selectedSnapshot?.scenario?.discountRate != null
                          ? `${(Number(selectedSnapshot.scenario.discountRate) * 100).toFixed(2)}%`
                          : "-"}
                      </div>
                    </div>
                    <div className="border rounded p-3">
                      <div className="text-xs text-gray-500">
                        Estimated Painted Area
                      </div>
                      <div className="font-semibold text-gray-900 mt-1">
                        {getEstimatedPaintedAreaText(selectedSnapshot)}
                      </div>
                    </div>
                    <div className="border rounded p-3">
                      <div className="text-xs text-gray-500">
                        Selected {getSelectedEntityDisplayLabel(selectedSnapshot)}
                      </div>
                      <div className="font-semibold text-gray-900 mt-1">
                        {getSelectedEntityCountValue(selectedSnapshot)}
                      </div>
                    </div>
                    <div className="border rounded p-3">
                      <div className="text-xs text-gray-500">Damage Split</div>
                      <div className="font-semibold text-gray-900 mt-1">
                        {getDamageSplitSummary(selectedSnapshot?.scenario)}
                      </div>
                    </div>
                  </div>
                </div>

                <div ref={budgetBarsSectionRef} className="bg-white rounded-lg border shadow-sm p-5 scroll-mt-28">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Budget Evolution (first 15 years)
                  </h3>
                  {detailLoading && (
                    <div className="text-sm text-gray-500">
                      Loading snapshot details...
                    </div>
                  )}
                  {!detailLoading && selectedYearlyTotals.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No yearly data found for this snapshot.
                    </div>
                  )}

                  {!detailLoading && selectedYearlyTotals.length > 0 && (
                    <div className="space-y-2">
                      {selectedYearlyTotals.map((row) => {
                        const value = Number(row.totalCost || 0);
                        const widthPct =
                          maxYearlyCost > 0
                            ? Math.max(2, (value / maxYearlyCost) * 100)
                            : 0;
                        return (
                          <div
                            key={row.yearIndex}
                            className="grid grid-cols-12 gap-3 items-center text-sm"
                          >
                            <div className="col-span-2 text-gray-600">
                              Year {row.yearIndex}
                            </div>
                            <div className="col-span-7 bg-gray-100 rounded h-5 overflow-hidden">
                              <div
                                className="h-5 bg-blue-500"
                                style={{ width: `${widthPct}%` }}
                              />
                            </div>
                            <div className="col-span-3 text-right font-medium text-gray-800 flex items-center justify-end gap-1">
                              <FontAwesomeIcon
                                icon={faDollarSign}
                                className="text-gray-400"
                              />
                              {formatCurrency(value)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div ref={budgetChartSectionRef} className="bg-white rounded-lg border shadow-sm p-5 scroll-mt-28">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Budget Evolution Chart
                  </h3>
                  {analyticsLoading && (
                    <div className="text-sm text-gray-500">
                      Loading chart analytics...
                    </div>
                  )}
                  {!analyticsLoading && budgetEvolutionData.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No budget evolution data for this snapshot.
                    </div>
                  )}
                  {!analyticsLoading && budgetEvolutionData.length > 0 && (
                    <div style={{ width: "100%", height: 320 }}>
                      <ResponsiveContainer>
                        <BarChart data={budgetEvolutionData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" />
                          <YAxis />
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                          <Legend />
                          <Bar
                            dataKey="costWash"
                            stackId="a"
                            fill="#60a5fa"
                            name="Wash"
                          />
                          <Bar
                            dataKey="costTU"
                            stackId="a"
                            fill="#34d399"
                            name="Touch-up"
                          />
                          <Bar
                            dataKey="costOC"
                            stackId="a"
                            fill="#fbbf24"
                            name="Overcoat"
                          />
                          <Bar
                            dataKey="costRE"
                            stackId="a"
                            fill="#f87171"
                            name="Recoat"
                          />
                          <Line
                            type="monotone"
                            dataKey="totalCost"
                            stroke="#1d4ed8"
                            strokeWidth={2}
                            dot={false}
                            name="Total Cost"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div ref={bridgeChartSectionRef} className="bg-white rounded-lg border shadow-sm p-5 scroll-mt-28">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Bridge Degradation Evolution
                  </h3>
                  <p className="mb-4 text-xs text-gray-500">
                    Weighted degradation % is the area-weighted average across bridge elements. It can match degraded area % when degraded area is derived directly from area × degradation % / 100.
                  </p>
                  {analyticsLoading && (
                    <div className="text-sm text-gray-500">
                      Loading degradation analytics...
                    </div>
                  )}
                  {!analyticsLoading &&
                    degradationEvolutionData.length === 0 && (
                      <div className="text-sm text-gray-500">
                        No degradation evolution data for this snapshot.
                      </div>
                    )}
                  {!analyticsLoading && degradationEvolutionData.length > 0 && (
                    <div style={{ width: "100%", height: 320 }}>
                      <ResponsiveContainer>
                        <LineChart data={degradationEvolutionData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" />
                          <YAxis
                            yAxisId="left"
                            tickFormatter={(value) => Number(value || 0).toFixed(2)}
                          />
                          <YAxis yAxisId="right" orientation="right" />
                          <Tooltip
                            formatter={(value, name) => [
                              formatBridgeChartMetric(value, name),
                              name,
                            ]}
                          />
                          <Legend />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="weightedDegradationPercent"
                            stroke="#dc2626"
                            strokeWidth={2}
                            dot={false}
                            name="Weighted Degradation %"
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="effectiveDegradedAreaPercent"
                            stroke="#ea580c"
                            strokeWidth={2}
                            dot={false}
                            name="Degraded Area %"
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="degradedArea"
                            stroke="#0284c7"
                            strokeWidth={2}
                            dot={false}
                            name="Degraded Area (m²)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div ref={heatmapSectionRef} className="bg-white rounded-lg border shadow-sm p-5 scroll-mt-28">
                  <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Direction Degradation Heatmap
                      </h3>
                      <p className="text-xs text-gray-500">
                        {heatmapMode === "d610"
                          ? "Values show D610 scores derived from the averaged child degradation percentages per direction and year."
                          : "Values show average child degradation percentage per direction and year, colored with the D610 settings palette."}
                      </p>
                    </div>
                    <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-1">
                      <button
                        type="button"
                        onClick={() => setHeatmapMode("percent")}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          heatmapMode === "percent"
                            ? "bg-white text-blue-700 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Current %
                      </button>
                      <button
                        type="button"
                        onClick={() => setHeatmapMode("d610")}
                        disabled={!hasD610HeatmapSettings || d610SettingsLoading}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          heatmapMode === "d610"
                            ? "bg-white text-blue-700 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                        title={
                          hasD610HeatmapSettings
                            ? "Show D610 score heatmap"
                            : "D610 settings are required for this view"
                        }
                      >
                        D610 Score
                      </button>
                    </div>
                  </div>
                  {analyticsLoading && (
                    <div className="text-sm text-gray-500">
                      Loading direction analytics...
                    </div>
                  )}
                  {!analyticsLoading && directionHeatmapRows.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No direction-level degradation data for this snapshot.
                    </div>
                  )}
                  {!analyticsLoading &&
                    heatmapMode === "d610" &&
                    !hasD610HeatmapSettings && (
                      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        D610 settings are required before the D610 Score heatmap can be shown.
                      </div>
                    )}

                  {!analyticsLoading && directionHeatmapDisplayRows.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs border border-gray-200">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-2 py-2 text-left border-b border-r">
                              Direction
                            </th>
                            {heatmapYears.map((year) => (
                              <th
                                key={year}
                                className="px-2 py-2 text-center border-b border-r"
                              >
                                {year}
                              </th>
                            ))}
                            <th className="px-2 py-2 text-center border-b">
                              {heatmapMode === "d610" ? "Avg Score" : "Avg %"}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {directionHeatmapDisplayRows.map((row) => (
                            <tr key={row.direction}>
                              <td className="px-2 py-2 border-r border-b font-semibold bg-gray-50">
                                {row.direction}
                              </td>
                              {(row.yearly || []).map((yearCell) => {
                                const value = Number(yearCell.displayValue ?? 0);
                                const shouldShowDash =
                                  heatmapMode === "percent" &&
                                  isNearZeroForDisplay(value, 2);
                                return (
                                  <td
                                    key={`${row.direction}_${yearCell.yearIndex}`}
                                    className="px-2 py-2 text-center border-r border-b"
                                    style={{
                                      backgroundColor: yearCell.backgroundColor,
                                      color: yearCell.textColor,
                                    }}
                                    title={yearCell.title}
                                  >
                                    {shouldShowDash ? "-" : value.toFixed(2)}
                                  </td>
                                );
                              })}
                              <td
                                className="px-2 py-2 text-center border-b font-medium"
                                style={{
                                  backgroundColor: row.averageBackgroundColor,
                                  color: row.averageTextColor,
                                }}
                                title={row.averageTitle}
                              >
                                {heatmapMode === "d610"
                                  ? Number(row.averageScore ?? 0).toFixed(2)
                                  : formatDisplayNumber(row.averagePercent ?? 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <button
                    onClick={() => navigate("/enhanced-simulation-results")}
                    className="bg-white rounded-lg border shadow-sm p-5 hover:shadow-md transition-shadow text-left group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                          <FontAwesomeIcon
                            icon={faChartLine}
                            className="h-6 w-6 text-blue-600"
                          />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Simulation Engine
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Run and save full-bridge simulations
                          </p>
                        </div>
                      </div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors"
                      />
                    </div>
                  </button>

                  <button
                    onClick={() => navigate("/repair-projects")}
                    className="bg-white rounded-lg border shadow-sm p-5 hover:shadow-md transition-shadow text-left group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                          <FontAwesomeIcon
                            icon={faWrench}
                            className="h-6 w-6 text-green-600"
                          />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Repair Projects
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Plan work based on saved snapshots
                          </p>
                        </div>
                      </div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors"
                      />
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {hoverPopup?.snapshot && (
          <div
            className="fixed z-50 w-80 bg-white border border-gray-200 rounded-md shadow-xl p-3"
            style={{ top: `${hoverPopup.top}px`, left: `${hoverPopup.left}px` }}
          >
            <div className="text-sm font-semibold text-gray-900">
              {formatSimulationName(
                hoverPopup.snapshot.name,
                hoverPopup.snapshot.id,
              )}
            </div>
            <div className="text-xs text-gray-500 mb-2">
              Simulation snapshot
            </div>
            <div className="space-y-1 text-xs text-gray-700">
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Date</span>
                <span>
                  {formatDate(
                    hoverPopup.snapshot.savedAt ||
                      hoverPopup.snapshot.createdAt,
                  )}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Time horizon</span>
                <span>
                  {hoverPopup.snapshot.scenario?.timeHorizonYears ?? "-"}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Inflation rate</span>
                <span>
                  {hoverPopup.snapshot.scenario?.discountRate != null
                    ? `${(Number(hoverPopup.snapshot.scenario.discountRate) * 100).toFixed(2)}%`
                    : "-"}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Estimated painted area</span>
                <span>
                  {getEstimatedPaintedAreaText(hoverPopup.snapshot)}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">
                  Selected {getSelectedEntityDisplayLabel(hoverPopup.snapshot)}
                </span>
                <span className="text-right">
                  {getSelectedEntityCountValue(hoverPopup.snapshot)}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Damage split</span>
                <span className="text-right">
                  {getDamageSplitSummary(hoverPopup.snapshot.scenario)}
                </span>
              </div>
            </div>
          </div>
        )}

        {hoveredSnapshotId && (
          <div className="text-xs text-gray-500 mt-3">
            Hover popups show scenario and selection values from each saved snapshot.
          </div>
        )}

        <div className="mt-4 text-xs text-gray-500">
          Snapshot selection in this dashboard supports comparing multiple saved
          simulation contexts over time.
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
